import { useState, useCallback, useMemo, memo } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItemWithSubmenu,
} from '@/shared/components';
import { ActionMenuItem } from './ActionMenuItem';
import { ActionMenuItemContent } from './ActionMenuItemContent';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
} from 'lucide-react';
import ArrowDownIcon from '@/shared/assets/arrow-down-icon.svg?react';
import ArrowDownUpIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import ArrowUpRightIcon from '@/shared/assets/arrow-top-right-icon.svg?react';
import PlusIcon from '@/shared/assets/plus-icon.svg?react';
import RefreshIcon from '@/shared/assets/exchange-process-icon.svg?react';
import ApiTokensIcon from '@/shared/assets/api-icon.svg?react';
import OpenNewTabRoundedIcon from '@/shared/assets/open-new-tab-rounded-icon.svg?react';
import type { AccountDetailsActionButtonsVisibility } from '@/widgets';
import {
  applyAmountPrecision,
  formatReceivedAmount,
  openTradingAccUrl,
  sum,
  useAppSettingsStore,
  useMediaQuery,
} from '@/shared/lib';
import type {
  AccountMarginRest,
  AccountRest,
  AccountType,
} from '@/entities/account';
import CandlesIcon from '@/shared/assets/candles-icon.svg?react';

const TRIGGER_CLASSNAME =
  'cursor-pointer p-0 relative top-[-1.2rem] right-[-0.8rem] dark:bg-transparent hover:bg-secondary-bg rounded-full w-[4.8rem] h-[4.8rem] flex items-center justify-center [&>div]:flex [&>div]:items-center [&>div]:justify-center';

type MenuAction = {
  key: string;
  visible: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  labelKey: string;
  onClick: () => void;
};

type Props = {
  type: AccountType;
  accountTitle: string;
  actionButtonsVisibility: AccountDetailsActionButtonsVisibility;
  onDeposit: () => void;
  onWithdraw: () => void;
  onInternalTransfer: () => void;
  onOpenNewAccount: () => void;
  onExchange: () => void;
  onAccountClick: (accountNumber: string) => void;
  accounts: Array<AccountRest | AccountMarginRest>;
  precision: number;
};

type AccountSubmenuRowProps = {
  accountTitle: string;
  account: AccountRest | AccountMarginRest;
  baseAsset: string;
  basePrecision: number;
  onSelect: (accountNumber: string) => void;
};

const AccountSubmenuRow = memo(function AccountSubmenuRow({
  accountTitle,
  account,
  baseAsset,
  basePrecision,
  onSelect,
}: AccountSubmenuRowProps) {
  const hasAssets = 'assets' in account && account.assets?.length;
  const hasBalanceAsset = 'balanceAsset' in account && account.balanceAsset;

  const firstAsset = hasAssets ? account.assets[0] : null;

  let precision = basePrecision;
  let assetCode = baseAsset;

  if (hasBalanceAsset) {
    const balanceAsset =
      'balanceAsset' in account ? account.balanceAsset : undefined;

    if (balanceAsset) {
      precision = balanceAsset.precision;
      assetCode = balanceAsset.code;
    }
  } else if (account.tradingProductCode === 'Invest' && firstAsset) {
    precision = firstAsset.precision ?? basePrecision;
    assetCode = firstAsset.code ?? baseAsset;
  }

  let amount = 0;

  if (hasAssets) {
    amount = applyAmountPrecision(
      sum(account.assets.map(a => a.baseAvailable ?? 0)),
      precision,
      'credit'
    );
  } else if ('equity' in account) {
    amount = account.equity ?? 0;
  }

  const isDisplayAmount = false;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(account.accNum);
    },
    [account.accNum, onSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(account.accNum);
      }
    },
    [account.accNum, onSelect]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex justify-between items-center w-full hover:bg-secondary-bg rounded-[1.2rem] px-[1.2rem] py-[0.8rem] cursor-pointer"
      aria-label={account.accNum}>
      <div className="flex flex-col justify-start">
        <span className="headline4 text-primary">
          {accountTitle} {account.tradingAccKey ?? ''}
        </span>
        {isDisplayAmount && (
          <span className="text-secondary p3">
            ≈ {formatReceivedAmount(amount)} {assetCode}
          </span>
        )}
      </div>
      <div className="flex items-center justify-center w-[2rem] h-[2rem]">
        <OpenNewTabRoundedIcon className="size-[2rem] text-tertiary" />
      </div>
    </div>
  );
});

export const AccountCardMenu = ({
  type,
  accountTitle,
  actionButtonsVisibility,
  onDeposit,
  onWithdraw,
  onInternalTransfer,
  onOpenNewAccount,
  onExchange,
  onAccountClick,
  accounts,
  precision,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { baseAsset } = useAppSettingsStore();
  const [open, setOpen] = useState(false);

  const closeAnd = useCallback(
    (fn: () => void) => () => {
      fn();
      setOpen(false);
    },
    []
  );

  const handleAccountClick = useCallback(
    (accountNumber: string) => {
      onAccountClick(accountNumber);
      setOpen(false);
    },
    [onAccountClick]
  );

  const menuActions = useMemo<MenuAction[]>(
    () => [
      {
        key: 'deposit',
        visible: actionButtonsVisibility.deposit,
        icon: ArrowDownIcon,
        labelKey: 'personalAccount.buttons.deposit',
        onClick: onDeposit,
      },
      {
        key: 'withdraw',
        visible: actionButtonsVisibility.withdraw,
        icon: ArrowUpRightIcon,
        labelKey: 'personalAccount.buttons.withdraw',
        onClick: onWithdraw,
      },
      {
        key: 'exchange',
        visible: actionButtonsVisibility.exchange,
        icon: RefreshIcon,
        labelKey: 'personalAccount.buttons.exchange',
        onClick: onExchange,
      },
      {
        key: 'transfer',
        visible: actionButtonsVisibility.transfer,
        icon: ArrowDownUpIcon,
        labelKey: 'personalAccount.buttons.transfer',
        onClick: onInternalTransfer,
      },
    ],
    [
      actionButtonsVisibility.openNewAccount,
      actionButtonsVisibility.deposit,
      actionButtonsVisibility.withdraw,
      actionButtonsVisibility.exchange,
      actionButtonsVisibility.transfer,
      onOpenNewAccount,
      onDeposit,
      onWithdraw,
      onExchange,
      onInternalTransfer,
    ]
  );

  const ChevronIcon = isMobile ? ChevronDownIcon : ChevronRightIcon;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger withChevron={false} className={TRIGGER_CLASSNAME}>
        <EllipsisVerticalIcon className="size-[2.4rem]" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        className="w-[26.8rem] min-w-[26.8rem] p-0 rounded-[1.6rem]"
        classNameViewport="w-full max-w-full p-[0.8rem]">
        {menuActions.map(
          ({ key, visible, icon, labelKey, onClick }) =>
            visible && (
              <ActionMenuItem
                key={key}
                icon={icon}
                label={t(labelKey)}
                onClick={closeAnd(onClick)}
              />
            )
        )}

        {accounts.length > 0 && actionButtonsVisibility.trade && (
          <DropdownMenuItemWithSubmenu
            className="h-[4.8rem] w-full rounded-[1.2rem] cursor-pointer pr-[0.5rem]"
            onClick={e => e.stopPropagation()}
            renderTrigger={() => (
              <ActionMenuItemContent
                icon={CandlesIcon}
                label={t('personalAccount.buttons.trade')}>
                <div className="flex items-center justify-center w-[2rem] h-[2rem]">
                  <ChevronIcon className="size-full text-tertiary stroke-[3]" />
                </div>
              </ActionMenuItemContent>
            )}
            // TODO: remove this when we have a better solution for mobile
            sideOffset={isMobile ? -265 : undefined}
            alignOffset={isMobile ? 200 : undefined}
            subContentClassName="z-2 relative">
            <div className="bg-popover w-[28.4rem] rounded-[1.6rem] p-[0.8rem] border shadow-md">
              {accounts.map(account => (
                <AccountSubmenuRow
                  key={account.accNum}
                  accountTitle={accountTitle}
                  account={account}
                  baseAsset={baseAsset}
                  basePrecision={precision}
                  onSelect={() =>
                    openTradingAccUrl(String(account.tradingAccKey), type)
                  }
                />
              ))}
            </div>
          </DropdownMenuItemWithSubmenu>
        )}

        {accounts.length > 0 && (
          <DropdownMenuItemWithSubmenu
            className="h-[4.8rem] w-full rounded-[1.2rem] cursor-pointer pr-[0.5rem]"
            onClick={e => e.stopPropagation()}
            renderTrigger={() => (
              <ActionMenuItemContent
                icon={ApiTokensIcon}
                label={t('personalAccount.buttons.webApiTokens')}>
                <div className="flex items-center justify-center w-[2rem] h-[2rem]">
                  <ChevronIcon className="size-full text-tertiary stroke-[3]" />
                </div>
              </ActionMenuItemContent>
            )}
            // TODO: remove this when we have a better solution for mobile
            sideOffset={isMobile ? -265 : undefined}
            alignOffset={isMobile ? 200 : undefined}
            subContentClassName="z-2 relative">
            <div className="bg-popover w-[28.4rem] rounded-[1.6rem] p-[0.8rem] border shadow-md">
              {accounts.map(account => (
                <AccountSubmenuRow
                  key={account.accNum}
                  accountTitle={accountTitle}
                  account={account}
                  baseAsset={baseAsset}
                  basePrecision={precision}
                  onSelect={handleAccountClick}
                />
              ))}
            </div>
          </DropdownMenuItemWithSubmenu>
        )}

        {actionButtonsVisibility.openNewAccount && (
          <ActionMenuItem
            key={'openNewAccount'}
            icon={PlusIcon}
            label={t('common.add')}
            onClick={closeAnd(onOpenNewAccount)}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
